import React, { useState, useEffect } from "react";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    TextField,
    Button,
    Checkbox,
    FormControlLabel,
    Box,
    Divider,
    IconButton,
    InputAdornment
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import styles from "./styles.module.css";
import { axiosProxyInstance } from "../../../utils/request";
import cogoToast from "cogo-toast";
import { useForm } from 'react-hook-form';
import { Controller } from "react-hook-form";
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import PhoneInput from 'react-phone-input-2';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

const schema = yup.object().shape({
  first_name: yup.string().required('First name is required')
                .max(100, 'Max 100 characters')
                .test(
                  "not-only-spaces",
                  "This field cannot contain only blank spaces",
                  (value) => value ? value.trim().length > 0 : true // Ensure non-blank content
                ),
  last_name: yup.string().required('Last name is required')
                .max(100, 'Max 100 characters')
                .test(
                  "not-only-spaces",
                  "This field cannot contain only blank spaces",
                  (value) => value ? value.trim().length > 0 : true // Ensure non-blank content
                ),
  email: yup.string().email('Invalid email address').required('Email is required'),
  phone: yup
    .string()
    .required('Phone number is required')
    .test('isValidPhone', 'Phone number must be exactly 10 digits', (value, context) => {
      if (!value) return false;

      const digitsOnly = value.replace(/\D/g, '');
      const country = context.parent.selectedCountry;

      if (!country || !country.dialCode) return false;

      const countryCode = country.dialCode;
      const localNumber = digitsOnly.startsWith(countryCode)
        ? digitsOnly.slice(countryCode.length)
        : digitsOnly;
      return localNumber.length === 10;
    }),
});

const passSchema = yup.object().shape({
  // 👇 Add these for password validation
  old_password: 
    yup.string().trim().required('Old password is required'),
  new_password: yup
    .string()
    .trim()
    .notRequired()
    .test(
        "not-only-spaces",
        "This field cannot contain only blank spaces",
        (value) => value ? value.trim().length > 0 : true // Ensure non-blank content
    )
    .test(
      "min-if-present",
      "Password must be at least 8 characters",
      (value) => !value || value.length >= 8 // only apply min length if provided
    )
    // .when('new_password', {
    //     is: (val) => val && val.length > 0,
    //     then: (schema) => schema
    //         .required('New password is required')
    //         .min(8, 'Password must be at least 8 characters')
    //         .matches(
    //             /[!@#$%^&*(),.?":{}|<>]/,
    //             'Password must contain at least one special character'
    //         ),
    // })
    
    // .min(8, 'Password must be at least 8 characters')
    .matches(
        /[!@#$%^&*(),.?":{}|<>]/,
        'Password must contain at least one special character'
    )
    .test(
      'password-different',
      'New password must be different from old password',
      function (value) {
        const { old_password } = this.parent;
        if (!value || !old_password) return true;
        return value !== old_password;
      }
    ),
});

const SettingsAccordion = () => {
    const [expandedPanels, setExpandedPanels] = useState([]);
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [preferedContact, setPreferedContact] = useState([]);
    const [phone, setPhone] = useState("");
    
    const currentUserId = localStorage.getItem("LPCurrentUserId");
    const [details, setDetails] = useState([]);
    const [defaultValue, setDefaultValues] = useState(null);
    const [passDefaultValue, setPassDefaultValues] = useState(null);

    // const fetchDetails = async () => {
    //     try {
    //         const response = await axiosProxyInstance.get(`/user/${currentUserId}`);
    //         setDetails(response.data.data ?? []);
    //         setLoading(false);
    //     } catch (err) {
    //         setError("Failed to fetch listings");
    //         setLoading(false);
    //     }
    // };

    const fetchDetails = async () => {
        try {
            const response = await axiosProxyInstance.get(`/user/${currentUserId}`);
            const userData = response.data.data ?? {};
            setDetails(userData);
            setPreferedContact(userData.prefered_contact || []);
            setLoading(false);
        } catch (err) {
            setError("Failed to fetch listings");
            setLoading(false);
        }
    };

    useEffect(() => {
        if(currentUserId){
            fetchDetails();
        }
    }, [currentUserId]);

    const form = useForm({
        mode: "onSubmit",
        resolver: yupResolver(schema)
    });

    const passForm = useForm({
        mode: "onSubmit",
        resolver: yupResolver(passSchema)
    });
    const { register, handleSubmit, setValue, control, formState: { errors, isDirty }, reset } = form;
    const { register: passRegister, handleSubmit: passHandleSubmit, setValue: passSetValue, control: passcontrol, formState: { errors: passErrors, isDirty: passIsDirty }, reset: passReset } = passForm;

    const handleChange = (panel) => () => {
        setExpandedPanels((prev) =>
            prev.includes(panel) ? prev.filter((p) => p !== panel) : [...prev, panel]
        );
    };

    const onSubmit = async (data) => {
        const payload = {
            btn_submit: true,
            provideruid: currentUserId,
            uid: currentUserId,
            first_name: data?.first_name || "",
            last_name: data?.last_name || "",
            mobile_number: data?.phone || "",
            user_email: data?.email || "",
            prefered_contact: preferedContact.length > 0 ? preferedContact : ['E-mail'],
        };
        try {
            const res = await axiosProxyInstance.post('/signup/updateuser/', payload);
            if (res?.data?.success){
                cogoToast.success("Settings Updated Successfully!");
                fetchDetails();
            }else {
                cogoToast.error("Submission failed:", res?.data?.data);
            }
        } catch (err) {
            console.error("Submission failed:", err);
        }
    }
    const onPasswordSubmit = async (data) => {
        const payload = {
            btn_submit: true,
            provideruid: currentUserId,
            uid: currentUserId,
            old_password: data?.old_password || "",
            new_password: data?.new_password || "",
        };
        try {
            const res = await axiosProxyInstance.post('/signup/updateuserpassword/', payload);
            if (res?.data?.success){
                cogoToast.success("Password Updated Successfully!");
                passSetValue("old_password", "")
                passSetValue("new_password", "")
            }else {
                cogoToast.error(res?.data?.data);
            }
        } catch (err) {
            console.error("Submission failed:", err);
        }
    }

    const onPreferenceSubmit = async (data) => {
        const payload = {
            btn_submit: true,
            provideruid: currentUserId,
            uid: currentUserId,
            first_name: details?.first_name || "",
            last_name: details?.last_name || "",
            mobile_number: details?.mobile_number || "",
            user_email: details?.user_email || "",
            prefered_contact: preferedContact.length > 0 ? preferedContact : [],
        };

        try {
            const res = await axiosProxyInstance.post('/signup/updateuser/', payload);
            if (res?.data?.success){
                cogoToast.success("Communication Preferences Updated Successfully!");
                fetchDetails();
            } else {
                cogoToast.error(res?.data?.data);
            }
        } catch (err) {
            console.error("Submission failed:", err);
        }
    }

    useEffect(() => {
        if (details && Object.keys(details).length) {
            setPhone(details?.mobile_number);
            const phoneNumber = parsePhoneNumberFromString(`+${details?.mobile_number}`);
            
            const values = {
                first_name: details.first_name || "",
                last_name: details.last_name || "",
                email: details.user_email || "",
                phone: details?.mobile_number || "",
                selectedCountry: {dialCode: phoneNumber.countryCallingCode},
                prefered_contact: details?.prefered_contact || [],
            };
            const passValues = {
                old_password: "",
                new_password: "",
            };
            reset(values);
            passReset(passValues);
            setDefaultValues(values);
            setPassDefaultValues(passValues);
        }
    }, [details, reset]);

    const handlePreferedContactChange = (event) => {
        const { name, checked } = event.target;
        const updated = checked
        ? [...preferedContact, name]
        : preferedContact.filter((item) => item !== name);
        setPreferedContact(updated);
        setValue("prefered_contact", updated, { shouldValidate: true, shouldDirty: true })
    };

    return (
        <Box className={styles.mainContainer}>
            <Typography variant="gothamXL" className={styles.settingsTitle}>Settings</Typography>
            <Box className={styles.settingsContainer}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Accordion
                        expanded={expandedPanels.includes("account")}
                        onChange={handleChange("account")}
                        className={styles.accordion}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} className={!expandedPanels.includes("account") ? styles.accordionSummary : ""}>
                            <Typography variant="gothamLarge" className={styles.accordionTitle}>Account Information</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography variant="gothamMedium">Update your account information.</Typography>
                            <Box className={styles.nameRow}>
                                <Controller
                                    name="first_name"
                                    control={control}
                                    defaultValue=""
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            label="First name"
                                            variant="outlined"
                                            className={styles.halfWidthInput}
                                            {...register('first_name')}
                                            error={!!errors.first_name}
                                            helperText={errors.first_name?.message}
                                        />
                                    )}
                                />
                                <Controller
                                    name="last_name"
                                    control={control}
                                    defaultValue=""
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            label="Surname"
                                            variant="outlined"
                                            className={styles.halfWidthInput}
                                            {...register('last_name')}
                                            error={!!errors.last_name}
                                            helperText={errors.last_name?.message}
                                        />
                                    )}
                                />
                            </Box>
                            <Controller
                                name="email"
                                control={control}
                                defaultValue=""
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Email"
                                        fullWidth
                                        variant="outlined"
                                        className={styles.inputField}
                                        {...register('email')}
                                        error={!!errors.email}
                                        helperText={errors.email?.message}
                                    />
                                )}
                            />
                            {/* <Controller
                                name="phone"
                                control={control}
                                defaultValue=""
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Phone number"
                                        fullWidth
                                        type="number"
                                        variant="outlined"
                                        className={styles.inputField}
                                        {...register('phone')}
                                        error={!!errors.phone}
                                        helperText={errors.phone?.message}
                                    />
                                )}
                            /> */}
                            <Box className={styles.phoneInputContainer}>
                                <PhoneInput
                                    country={'gb'}
                                    value={phone}
                                    onChange={(value, country) => {
                                        setPhone(value);
                                        setValue("phone", value, { shouldValidate: true, shouldDirty: true });
                                        setValue("selectedCountry", country, {shouldDirty: true});
                                    }}
                                    inputProps={{
                                        name: 'phone',
                                        required: true,
                                    }}
                                    containerClass={styles.phoneInputWrapper}
                                    inputClass={styles.phoneInputBox}
                                />

                                {errors.phone && (
                                    <Typography variant='gothamSmall' color="error" className={styles.errorText}>
                                        {errors.phone.message}
                                    </Typography>
                                )}
                            </Box>
                            <Box className={styles.buttonGroup}>
                                <Button disabled={!isDirty} variant="contained" type="submit" className={styles.saveButton}>Save changes</Button>
                                <Button 
                                    disabled={!isDirty}
                                    variant="text" className={styles.cancelButton}
                                    onClick={() => {
                                        setPhone(defaultValue.phone);
                                        reset(defaultValue)
                                    }}
                                >
                                    Cancel
                                </Button>
                            </Box>
                        </AccordionDetails>
                        <Divider className="margin-top-15" />
                    </Accordion>
                </form>
                {/* Password */}
                <form onSubmit={passHandleSubmit(onPasswordSubmit)}>

                    <Accordion
                        expanded={expandedPanels.includes("password")}
                        onChange={handleChange("password")}
                        className={styles.accordion}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} className={!expandedPanels.includes("password") ? styles.accordionSummary : ""}>
                            <Typography variant="gothamLarge" className={styles.accordionTitle}>Password</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography variant="gothamMedium">Manage your password.</Typography>
                            <TextField
                                fullWidth
                                label="Old password"
                                variant="outlined"
                                {...passRegister('old_password')}
                                className={styles.inputField}
                                type={showOldPassword ? "text" : "password"}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowOldPassword(!showOldPassword)} edge="end">
                                                {showOldPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                                error={!!passErrors.old_password}
                                helperText={passErrors.old_password?.message}
                            />
                            <TextField
                                fullWidth
                                label="New password"
                                variant="outlined"
                                className={styles.inputField}
                                type={showNewPassword ? "text" : "password"}
                                {...passRegister('new_password')}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
                                                {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                                error={!!passErrors.new_password}
                                helperText={passErrors.new_password?.message}
                            />
                            <Typography variant="gothamSmall" className={styles.passwordNote}>
                            <InfoOutlineIcon className={styles.infoIcon}/> Must contain at least 8 characters and 1 special character.
                            </Typography>
                            <Box className={styles.buttonGroup}>
                                <Button variant="contained" type="submit" className={styles.saveButton}>Save changes</Button>
                                <Button 
                                    disabled={!passIsDirty}
                                    variant="text" className={styles.cancelButton}
                                    onClick={() => passReset(passDefaultValue)}
                                >
                                    Cancel
                                </Button>
                            </Box>
                        </AccordionDetails>
                        <Divider className="margin-top-15" />
                    </Accordion>
                </form>
                {/* Communication Preferences */}
                <form onSubmit={handleSubmit(onPreferenceSubmit)}>
                    <Accordion
                        expanded={expandedPanels.includes("communication")}
                        onChange={handleChange("communication")}
                        className={styles.accordion}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} className={!expandedPanels.includes("communication") ? styles.accordionSummary : ""}>
                            <Typography variant="gothamLarge" className={styles.accordionTitle}>Communication Preferences</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography className={styles.contactText}>How would you like to be contacted?</Typography>
                            <FormControlLabel
                                className={styles.checkboxContainer}
                                control={
                                    <Checkbox
                                        name="E-mail"
                                        checked={preferedContact.includes("E-mail")}
                                        onChange={handlePreferedContactChange}
                                    />
                                }
                                label="E-mail"
                            />
                            <FormControlLabel
                                className={styles.checkboxContainer}
                                control={
                                    <Checkbox
                                        name="Phone number"
                                        checked={preferedContact.includes("Phone number")}
                                        onChange={handlePreferedContactChange}
                                    />
                                }
                                label="Phone number"
                            />
                            <Box className={styles.buttonGroup}>
                                <Button disabled={!isDirty} variant="contained" type="submit" className={styles.saveButton}>Save changes</Button>
                                <Button 
                                    disabled={!isDirty}
                                    variant="text" className={styles.cancelButton}
                                    onClick={() => {
                                        reset(defaultValue);
                                        setPreferedContact(details?.prefered_contact);
                                    }}
                                >Cancel</Button>
                            </Box>
                        </AccordionDetails>
                        <Divider className={styles.divider} />
                    </Accordion>
                </form>
            </Box>
        </Box>
    );
};

export default SettingsAccordion;
